import styled from 'styled-components';
import useResponsive from 'src/utils/device';

type Props = {
  hidden?: boolean;
  showHeader?: boolean;
};

const COLUMNIST_CARDS_DESKTOP = 6;
const COLUMNIST_CARDS_MOBILE_ROW = 3;

const SkeletonColumnist = ({ hidden = false, showHeader = true }: Props) => {
  const responsive = useResponsive();
  const columnCount = responsive.isMobile ? 2 : 4;
  return (
    <SkeletonWrapper aria-hidden='true' $hidden={hidden}>
      {showHeader && (
        <div className='sk-header'>
          <span className='sk-line' />
          <span className='sk sk-header-title' />
          <span className='sk-line' />
        </div>
      )}

      <div className='container'>
        {/* ================= Desktop: Columnist Grid 3x2 ================= */}
        <div className='columnist-grid isDesktop'>
          {Array.from({ length: COLUMNIST_CARDS_DESKTOP }).map((_, i) => (
            <div key={i} className='sk-card-columnist'>
              <span className='sk sk-thumb' />
              <div className='sk-card-body'>
                <span className='sk-tag' />
                <span className='sk sk-text sk-w-85' />
                <span className='sk sk-text sk-w-85' />
              </div>
            </div>
          ))}
        </div>

        {/* ================= Mobile: Columnist Scroll Rows =============== */}
        <div className='columnist-mobile isMobile'>
          {[0, 1].map(row => (
            <div key={row} className='columnist-scroll-row'>
              {Array.from({ length: COLUMNIST_CARDS_MOBILE_ROW }).map((_, i) => (
                <div key={i} className='columnist-scroll-item'>
                  <div className='sk-card-tiny'>
                    <span className='sk sk-thumb-tiny' />
                    <div className='sk-card-body'>
                      <span className='sk-tag' />
                      <span className='sk sk-text sk-w-100' />
                      <span className='sk sk-text sk-w-70' />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* ================= Column Items (grid mode) ==================== */}
        <div className='column-item-grid'>
          {Array.from({ length: columnCount }).map((_, i) => (
            <div key={i} className='column-item'>
              <span className='sk sk-text sk-w-80 sk-center' />
              <span className='sk sk-avatar' />
            </div>
          ))}
        </div>
      </div>
    </SkeletonWrapper>
  );
};

const SkeletonWrapper = styled.div<{ $hidden: boolean }>`
  padding: 30px 0;
  padding-top: 0;

  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  transition:
    opacity 0.25s ease,
    visibility 0s linear ${({ $hidden }) => ($hidden ? '0.25s' : '0s')};
  pointer-events: none;

  .container {
    padding: 0 15px;
    @media (max-width: 768px) {
      padding: 0 10px;
    }
  }

  /* ---------------------------- Shimmer base ---------------------------- */
  .sk {
    display: block;
    background: #e9e9e9;
    background-image: linear-gradient(90deg, #e9e9e9 0%, #f4f4f4 40%, #e9e9e9 80%);
    background-size: 200% 100%;
    animation: skShimmer 1.4s ease-in-out infinite;
    border-radius: 8px;
  }

  @keyframes skShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .sk {
      animation: none;
      background-image: none;
    }
  }

  .sk-text {
    height: 15px;
    border-radius: 4px;
  }
  .sk-w-100 {
    width: 100%;
  }
  .sk-w-85 {
    width: 85%;
  }
  .sk-w-80 {
    width: 50%;
  }
  .sk-w-70 {
    width: 70%;
  }
  .sk-w-60 {
    width: 60%;
  }
  .sk-center {
    margin: 0 auto;
  }

  .sk-tag {
    width: 84px;
    height: 20px;
    border-radius: 4px;
  }

  /* ------------------------------ Header -------------------------------- */
  /* จำลอง WidgetHeader variant='line' position='center' */
  .sk-header {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 15px 30px;

    .sk-line {
      flex: 1;
      height: 1px;
      background: #5f5f5f42;
    }
    .sk-header-title {
      width: 260px;
      height: 42px;
      flex: 0 0 auto;
      @media (max-width: 1024px) {
        width: 200px;
        height: 34px;
      }
      @media (max-width: 768px) {
        width: 150px;
        height: 26px;
      }
    }
  }

  /* ------------------------ Desktop/Mobile toggle ----------------------- */
  .isDesktop {
    display: grid;
    @media (max-width: 768px) {
      display: none;
    }
  }

  .isMobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }

  /* --------------------- Desktop: Columnist Grid 3x2 -------------------- */
  .columnist-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin: 30px 0;
    min-height: 344px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* CARD_SMALL = thumbnail ซ้าย + เนื้อหาขวา                                */
  /* 344px - gap 20px = 324 / 2 แถว = 162px ต่อการ์ด                          */
  .sk-card-columnist {
    display: flex;
    gap: 12px;
    height: 162px;
    border: 1px solid #e9e9e9;
    padding: 15px;
    border-radius: 30px;
  }

  .sk-thumb {
    flex: 0 0 35%;
    height: 100%;
    border-radius: 50%;
    aspect-ratio: 1 / 1;
  }

  .sk-card-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 4px;
    min-width: 0;
  }

  /* --------------------- Mobile: Columnist Scroll ----------------------- */
  .columnist-mobile {
    margin: 20px 0;
    min-height: 244px;
  }

  .columnist-scroll-row {
    display: flex;
    gap: 12px;
    overflow: hidden; /* skeleton ไม่ต้อง scroll */
    padding: 5px 0 15px;
    padding-left: 10px;
    padding-right: 10px;
  }

  .columnist-scroll-item {
    flex-shrink: 0;
    width: 280px;
    @media (max-width: 400px) {
      width: 260px;
      border: 1px solid #e9e9e9;
      padding: 15px;
      border-radius: 30px;
    }
  }

  /* CARD_TINY: 244px - padding รวม 40px = ~102px ต่อแถว */
  .sk-card-tiny {
    display: flex;
    gap: 10px;
    height: 102px;
  }

  .sk-thumb-tiny {
    flex: 0 0 100px;
    height: 100%;
    border-radius: 50%;
  }

  /* ------------------------- Column Item Grid --------------------------- */
  .column-item-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, 300px);
    gap: 15px;
    margin-top: 30px;
    justify-content: center;
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, 170px);
    }
  }

  .column-item {
    border-radius: 12px;
    overflow: hidden;
    padding: 25px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    @media (max-width: 768px) {
      border-radius: 10px;
      padding: 15px;
    }
  }

  /* CARD_MEDIUM ในโหมด grid — รูปคอลัมนิสต์ + ชื่อ */
  .sk-avatar {
    width: 100%;
    aspect-ratio: 1 / 1;
    max-width: 250px;

    @media (max-width: 768px) {
      max-width: 140px;
    }
  }
`;

export default SkeletonColumnist;
